import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Ticket, XCircle, Loader } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authed = () => {
  const t = localStorage.getItem("admin_session_token");
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const STATUS_OPTIONS = ["", "PENDING", "ACTIVE", "REDEEMED", "CANCELLED", "EXPIRED"];
const STATUS_CLASS = {
  ACTIVE: "status-ok",
  PENDING: "status-wait",
  REDEEMED: "status-done",
  EXPIRED: "status-cold",
  CANCELLED: "status-cold",
};

export default function AdminVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q.trim()) params.q = q.trim();
      const r = await axios.get(`${API}/admin/vouchers`, { ...authed(), params });
      setVouchers(r.data.vouchers || []);
    } catch (err) {
      console.error("Failed to load vouchers:", err);
    } finally {
      setLoading(false);
    }
  }

  // Intentional: load on mount only. Subsequent reloads happen via the "Apply" button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function cancel(id) {
    if (!window.confirm("Cancel this voucher? The guest will no longer be able to redeem it.")) return;
    try {
      await axios.patch(`${API}/admin/vouchers/${id}/cancel`, null, authed());
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Cancel failed.");
    }
  }

  return (
    <div data-testid="admin-vouchers">
      <p className="eyebrow"><Ticket size={13} /> Gift vouchers</p>
      <h1>Vouchers</h1>
      <p className="muted">Every voucher sold through the public site. Filter by status or search by recipient / sender / code.</p>

      <form
        onSubmit={(e) => { e.preventDefault(); load(); }}
        className="admin-toolbar"
        style={{ gap: 10, alignItems: "end" }}
      >
        <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 700, color: "var(--muted)" }}>
          STATUS
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="voucher-filter-status">
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s || "All"}</option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 700, color: "var(--muted)", flex: 1, minWidth: 220 }}>
          SEARCH
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Code, recipient or sender name"
            data-testid="voucher-filter-q"
          />
        </label>
        <button type="submit" className="btn btn-primary" data-testid="voucher-filter-apply">
          <Search size={14} /> Apply
        </button>
      </form>

      {loading ? (
        <p className="muted"><Loader size={14} className="spin" /> Loading…</p>
      ) : vouchers.length === 0 ? (
        <p className="muted">No vouchers match these filters yet.</p>
      ) : (
        <table className="admin-table" data-testid="admin-vouchers-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Service</th>
              <th>Recipient</th>
              <th>Sender</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Issued</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {vouchers.map((v) => (
              <tr key={v.id}>
                <td><code>{v.voucher_code}</code></td>
                <td>{v.service_name}<br /><span className="muted-small">{v.duration}</span></td>
                <td>{v.recipient_name}<br /><span className="muted-small">{v.recipient_mobile}</span></td>
                <td>{v.sender_name}<br /><span className="muted-small">{v.sender_contact}</span></td>
                <td>₹{v.amount?.toLocaleString("en-IN")}</td>
                <td><span className={`status-pill ${STATUS_CLASS[v.status] || ""}`}>{v.status}</span></td>
                <td>{v.issue_date?.slice(0, 10)}</td>
                <td>
                  {["ACTIVE", "PENDING"].includes(v.status) && (
                    <button
                      type="button"
                      className="icon-btn danger"
                      onClick={() => cancel(v.id)}
                      aria-label="Cancel voucher"
                      data-testid={`admin-voucher-cancel-${v.id}`}
                    >
                      <XCircle size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
